<script setup lang="ts">
/**
 * Entry point for a payment the participant starts themselves.
 *
 * Reproduces the chosen treatment from the certifid-100-vision prototype
 * (`app/components/portal/buyer-initiated/MakePaymentCard.vue` on main): a solid
 * brand-primary wallet chip, a title, a purpose hint, and a chevron, on an
 * outline stroke with no elevation "so it reads as an invitation, not an
 * assigned task".
 *
 * The whole card is one button. It deliberately does not carry a TaskCard-style
 * CTA: an assigned task has an action you owe, this is an offer you may take.
 * That is also why there is no elevation at any breakpoint, where TaskCard is
 * shadowed on mobile.
 *
 * Copy is taken verbatim from the prototype's en.json
 * (portal.buyerInitiated.cardTitle / cardSubtitle). The prototype's
 * coming-soon badge is not reproduced: it gates a user-testing build, not this.
 */
defineProps<{
    errorMessage?: string | null;
}>();

const emit = defineEmits<{
    open: [];
}>();
</script>

<template>
    <div>
        <button
            type="button"
            class="participant-payment-card"
            data-test="participant-payment-card"
            @click="emit('open')"
        >
            <span class="participant-payment-card__chip" aria-hidden="true">
                <app-icon icon="cd-ic-wallet" />
            </span>
            <span class="participant-payment-card__content">
                <span class="participant-payment-card__title">Send a payment</span>
                <span class="participant-payment-card__subtitle">
                    Cash to close, earnest money, or something else
                </span>
            </span>
            <app-icon icon="cd-ic-caret-right" class="participant-payment-card__chevron" aria-hidden="true" />
        </button>

        <p
            v-if="errorMessage"
            class="participant-payment-card__error"
            role="alert"
            aria-live="polite"
            data-test="participant-payment-card-error">
            {{ errorMessage }}
        </p>
    </div>
</template>

<style lang="scss" scoped>
@use 'certifid-v2-client-hub-styles' as *;

.participant-payment-card {
    display: flex;
    align-items: center;
    gap: $portal-card-gap;
    width: 100%;
    text-align: left;
    padding: $portal-card-padding;
    border-radius: $portal-card-radius;
    background: $white;
    // Buttons don't inherit the page font-family by default.
    font-family: inherit;
    // Outline stroke, no elevation, at every breakpoint. TaskCard is shadowed on
    // mobile; matching it here would make the invitation read as an assigned task.
    border: 1px solid $portal-border;
    box-shadow: none;
    cursor: pointer;
    transition: border-color 0.15s ease, transform 0.15s ease;

    &:hover {
        border-color: $black-low;
    }

    &:active {
        transform: scale(0.98);
    }

    &:focus-visible {
        outline: 2px solid $theme-primary;
        outline-offset: 2px;
    }

    &__chip {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        // $portal-card-icon is 44px, 50px from tablet up, and matches the
        // prototype's --portal-card-icon exactly.
        width: $portal-card-icon;
        height: $portal-card-icon;
        border-radius: 50%;
        background: $theme-primary;

        // app-icon renders an icomoon glyph in an <i>, not an svg, so the glyph
        // is sized with font-size rather than width and height.
        :deep(i) {
            font-size: 20px;
            // The prototype marks this text-portal-primary-light, a tint rather
            // than pure white, against the solid brand chip.
            color: $theme-primary-lightest;
        }
    }

    &__content {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 2px;
    }

    &__title {
        font-size: $text-portal-body-lg;
        font-weight: 700;
        // Tailwind leading-snug, as the prototype's card title uses.
        line-height: 1.375;
        color: $portal-text;
    }

    &__subtitle {
        font-size: $text-portal-caption;
        color: $portal-text-mid;
    }

    &__chevron {
        flex-shrink: 0;

        :deep(i) {
            font-size: 20px;
            color: $portal-text-mid;
        }
    }

    &__error {
        font-size: $text-portal-small;
        color: $red;
        margin: 8px 0 0;
    }
}
</style>
